import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  addSongToPlaylist,
  getPlaylistItemsSpotify,
} from '../utils';
import {
  loadAccountOwnedPlaylistOptions,
  loadCampaignAcceptPlaylists,
  loadProfileSpotifySessions,
  type CampaignAcceptPlaylistOption,
  type ProfileSpotifySession,
} from '../utils/campaignAcceptPlaylists';
import {
  notifyCampaignSongPlacements,
  type CampaignSongPlacement,
} from '../utils/campaignAcceptNotify';
import type { LinkedCuratorProfile } from '../utils/accountCuratorOffer';
import { fetchLinkedCuratorProfiles } from '../services/curatorAccountService';
import {
  formatPlaylistAddError,
  resolvePlacementStartTime,
  resolveStartTimeForApi,
  validateAddToPlaylistForm,
} from '../utils/addToPlaylistForm';
import {
  getAuthAccountUserId,
  getSiblingProfileObjectIdForAdd,
  resolveActiveProfileId,
} from '../utils/authAccount';

const EMPTY_LINKED_PROFILES: LinkedCuratorProfile[] = [];

interface LoginSession {
  token: string;
  refreshToken: string;
  userId: string;
}

interface UseAccountCampaignAcceptOptions {
  isModalVisible?: boolean;
  isFromCampaign?: boolean;
  campaignData?: {
    _id?: string;
    track_released?: boolean;
    spotify_uri?: string;
    allocationMode?: string;
    assignedProfileIds?: string[];
    curatorIds?: string[];
    curatorAllocations?: Array<{
      _id?: string;
      winnerId?: string;
      curatorIds?: string[];
    }>;
  } | null;
  linkedProfiles: LinkedCuratorProfile[];
  accountUserId?: string;
  loginState: LoginSession;
  release: string;
  setDate: Date | null;
  note: string;
  userName?: string;
  totalPlaylist: Array<{ id: string; name?: string; external_urls?: { spotify?: string } }>;
  setLoading: (loading: boolean) => void;
}

export const useAccountCampaignAccept = ({
  isModalVisible,
  isFromCampaign,
  campaignData,
  linkedProfiles,
  accountUserId,
  loginState,
  release,
  setDate,
  note,
  userName,
  totalPlaylist,
  setLoading,
}: UseAccountCampaignAcceptOptions) => {
  const profilesFetchStartedRef = useRef(false);
  const accountPlaylistsLoadedKeyRef = useRef<string | null>(null);
  const campaignPlaylistsLoadedKeyRef = useRef<string | null>(null);
  const profilesFetchRequestIdRef = useRef(0);

  const [accountPlaylistOptions, setAccountPlaylistOptions] = useState<
    CampaignAcceptPlaylistOption[]
  >([]);
  const [profileSessions, setProfileSessions] = useState<Record<string, ProfileSpotifySession>>({});
  const [accountPlaylistsLoading, setAccountPlaylistsLoading] = useState(false);
  const [profilesResolving, setProfilesResolving] = useState(false);
  const [resolvedProfiles, setResolvedProfiles] = useState<LinkedCuratorProfile[]>(() =>
    linkedProfiles.length > 0 ? linkedProfiles : []
  );
  const [resolvedAccountUserId, setResolvedAccountUserId] = useState<string | undefined>(() =>
    accountUserId || getAuthAccountUserId() || undefined
  );

  const linkedProfileIdsKey = useMemo(
    () => linkedProfiles.map(profile => profile.id).sort().join(','),
    [linkedProfiles]
  );

  useEffect(() => {
    if (linkedProfiles.length > 0) {
      setResolvedProfiles(linkedProfiles);
    }
  }, [linkedProfiles]);

  useEffect(() => {
    if (accountUserId) {
      setResolvedAccountUserId(accountUserId);
    }
  }, [accountUserId]);

  useEffect(() => {
    if (!isModalVisible) {
      profilesFetchStartedRef.current = false;
      accountPlaylistsLoadedKeyRef.current = null;
      campaignPlaylistsLoadedKeyRef.current = null;
      setProfilesResolving(false);
      return;
    }

    const authAccountUserId = accountUserId || getAuthAccountUserId();
    if (!authAccountUserId) {
      setProfilesResolving(false);
      setResolvedProfiles(linkedProfiles.length > 0 ? linkedProfiles : []);
      return;
    }

    setResolvedAccountUserId(prev => prev || authAccountUserId);

    const requestId = profilesFetchRequestIdRef.current + 1;
    profilesFetchRequestIdRef.current = requestId;
    let cancelled = false;
    setProfilesResolving(true);

    fetchLinkedCuratorProfiles(authAccountUserId)
      .then(profiles => {
        if (cancelled || profilesFetchRequestIdRef.current !== requestId) {
          return;
        }
        setResolvedProfiles(profiles);
        setResolvedAccountUserId(authAccountUserId);
      })
      .catch(error => {
        if (cancelled || profilesFetchRequestIdRef.current !== requestId) {
          return;
        }
        console.error('Failed to load linked profiles for playlist picker:', error);
        if (linkedProfiles.length > 0) {
          setResolvedProfiles(linkedProfiles);
        }
      })
      .finally(() => {
        if (!cancelled && profilesFetchRequestIdRef.current === requestId) {
          setProfilesResolving(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountUserId, isModalVisible, linkedProfileIdsKey]);

  const activeAccountUserId = resolvedAccountUserId || accountUserId;
  const activeProfiles = useMemo(() => {
    if (resolvedProfiles.length > 0) {
      return resolvedProfiles;
    }
    if (linkedProfiles.length > 0) {
      return linkedProfiles;
    }
    return EMPTY_LINKED_PROFILES;
  }, [linkedProfiles, resolvedProfiles]);

  const activeProfileIdsKey = useMemo(
    () => activeProfiles.map(profile => profile.id).sort().join(','),
    [activeProfiles]
  );

  const usesAccountCampaignPlaylists = useMemo(
    () =>
      isFromCampaign === true &&
      !!activeAccountUserId &&
      activeProfiles.length > 0 &&
      !!campaignData,
    [activeAccountUserId, activeProfiles.length, campaignData, isFromCampaign]
  );

  const usesAccountWidePlaylists = useMemo(
    () =>
      isFromCampaign !== true &&
      !!activeAccountUserId &&
      activeProfiles.length > 1,
    [activeAccountUserId, activeProfiles.length, isFromCampaign]
  );

  const usesMultiProfilePlaylistMode =
    usesAccountCampaignPlaylists || usesAccountWidePlaylists;

  const isAccountPlaylistPickerLoading =
    accountPlaylistsLoading || profilesResolving;

  const getCampaignId = useCallback(() => campaignData?._id || null, [campaignData?._id]);

  const resetCampaignAcceptState = useCallback(() => {
    setAccountPlaylistOptions([]);
    setProfileSessions({});
    setAccountPlaylistsLoading(false);
    setProfilesResolving(false);
    profilesFetchStartedRef.current = false;
    accountPlaylistsLoadedKeyRef.current = null;
    campaignPlaylistsLoadedKeyRef.current = null;
    profilesFetchRequestIdRef.current += 1;
  }, []);

  const activeProfileId = useMemo(
    () => resolveActiveProfileId(activeProfiles),
    [activeProfiles]
  );

  const getPlaylistSession = useCallback(
    (playlistId: string): ProfileSpotifySession | null => {
      const option = accountPlaylistOptions.find(item => item.playlistId === playlistId);
      if (!option) {
        return null;
      }

      const cachedSession = profileSessions[option.profileId];
      if (cachedSession) {
        return cachedSession;
      }

      if (activeProfileId && option.profileId === activeProfileId) {
        return {
          profileId: activeProfileId,
          accessToken: loginState.token,
          spotifyUserId: loginState.userId,
        };
      }

      return null;
    },
    [
      accountPlaylistOptions,
      activeProfileId,
      loginState.token,
      loginState.userId,
      profileSessions,
    ]
  );

  useEffect(() => {
    if (usesMultiProfilePlaylistMode) {
      return;
    }

    setAccountPlaylistOptions([]);
    setProfileSessions({});
    accountPlaylistsLoadedKeyRef.current = null;
    campaignPlaylistsLoadedKeyRef.current = null;
  }, [usesMultiProfilePlaylistMode]);

  useEffect(() => {
    const loadCampaign = usesAccountCampaignPlaylists && campaignData;
    const loadAccountWide = usesAccountWidePlaylists;
    if (!isModalVisible || !activeAccountUserId || (!loadCampaign && !loadAccountWide)) {
      return;
    }

    const loadKey = loadCampaign
      ? `campaign:${activeAccountUserId}:${activeProfileIdsKey}:${campaignData._id || ''}`
      : `account:${activeAccountUserId}:${activeProfileIdsKey}`;
    const loadedKeyRef = loadCampaign
      ? campaignPlaylistsLoadedKeyRef
      : accountPlaylistsLoadedKeyRef;

    if (loadedKeyRef.current === loadKey) {
      return;
    }

    loadedKeyRef.current = loadKey;
    let cancelled = false;

    const loadPlaylists = async () => {
      setAccountPlaylistsLoading(true);
      try {
        const options = loadCampaign
          ? await loadCampaignAcceptPlaylists(
              campaignData,
              activeProfiles,
              activeAccountUserId
            )
          : await loadAccountOwnedPlaylistOptions(activeProfiles, activeAccountUserId);
        if (cancelled) return;

        setAccountPlaylistOptions(options);
        setAccountPlaylistsLoading(false);

        const profileIds = Array.from(new Set(options.map(option => option.profileId)));
        if (!profileIds.length) {
          return;
        }

        const activeProfileIdForSessions = resolveActiveProfileId(activeProfiles);
        const sessions = await loadProfileSpotifySessions(
          profileIds,
          activeAccountUserId,
          activeProfileIdForSessions,
          activeProfileIdForSessions
            ? {
                accessToken: loginState.token,
                spotifyUserId: loginState.userId,
              }
            : undefined
        );

        if (!cancelled) {
          setProfileSessions(sessions);
        }
      } catch (error) {
        console.error(
          loadCampaign
            ? 'Failed to load account campaign playlists:'
            : 'Failed to load account-wide playlists:',
          error
        );
        loadedKeyRef.current = null;
        if (!cancelled) {
          toast.error(
            loadCampaign
              ? 'Could not load playlists for your assigned profiles.'
              : 'Could not load playlists for your linked profiles.'
          );
          setAccountPlaylistsLoading(false);
        }
      }
    };

    loadPlaylists();

    return () => {
      cancelled = true;
      if (loadedKeyRef.current === loadKey) {
        loadedKeyRef.current = null;
      }
    };
  }, [
    activeAccountUserId,
    activeProfileIdsKey,
    activeProfiles,
    campaignData,
    isModalVisible,
    loginState.token,
    loginState.userId,
    usesAccountCampaignPlaylists,
    usesAccountWidePlaylists,
  ]);

  const mapCampaignPlaylistItems = useCallback(
    (playlists: Array<{ id: string; name?: string; owner?: { display_name?: string } }>) => {
      if (usesMultiProfilePlaylistMode) {
        if (!accountPlaylistOptions.length) {
          return [];
        }

        const profileCount = new Set(accountPlaylistOptions.map(option => option.profileId)).size;
        return accountPlaylistOptions.map(option => ({
          key: option.playlistId,
          value: profileCount > 1 ? `${option.name} (${option.profileUsername})` : option.name,
        }));
      }

      const playlistItems: Array<{ key: string; value: string }> = [];
      playlists.forEach(item => {
        const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
        if (item?.owner?.display_name === spotifyUser?.username) {
          playlistItems.push({
            key: item.id,
            value: item.name || item.id,
          });
        }
      });
      return playlistItems;
    },
    [accountPlaylistOptions, usesMultiProfilePlaylistMode]
  );

  const notifyBackendAboutAddedSong = useCallback(
    async (songInfoInput: CampaignSongPlacement | CampaignSongPlacement[]) => {
      if (!isFromCampaign) {
        return;
      }

      const placements = Array.isArray(songInfoInput) ? songInfoInput : [songInfoInput];
      try {
        await notifyCampaignSongPlacements({
          placements,
          campaignData,
          refreshToken: loginState.refreshToken,
          usesAccountCampaignPlaylists,
        });
      } catch (error: any) {
        console.error('Failed to notify backend about the added song:', error);
        if (error.response) {
          toast.error(
            `Failed to update campaign status: ${error.response.data?.message || 'Unknown error'}`
          );
        } else if (error.request) {
          toast.error('Failed to connect to backend - campaign status not updated');
        } else {
          toast.error('Failed to update campaign status');
        }
      }
    },
    [campaignData, isFromCampaign, loginState.refreshToken, usesAccountCampaignPlaylists]
  );

  const addCampaignSongToSelectedPlaylists = useCallback(
    async (values: {
      position: number | string;
      playlistId: string | string[];
      searchedSongData: string;
      artist_name?: string;
      song_name?: string;
      startTime?: string;
      endTime?: string;
    }): Promise<CampaignSongPlacement | null> => {
      const playlistIds = Array.isArray(values.playlistId)
        ? values.playlistId.filter(Boolean)
        : values.playlistId
          ? [values.playlistId]
          : [];

      const validation = validateAddToPlaylistForm({
        playlistIds,
        position: values.position,
        usesNumberPositionInput: true,
        searchedSongData: values.searchedSongData,
        isFromCampaign,
        endTime: values.endTime,
      });

      if (validation.ok === false) {
        toast.error(validation.message);
        setLoading(false);
        return null;
      }

      const resolvedPosition = validation.position;

      const isFuturePlaylistPage = window.location.pathname === '/future_playlist';
      const timingMode = isFuturePlaylistPage ? 'future' : release === 'Later' ? 'future' : 'past';
      const isCurrentlyUnreleased = !!(
        isFromCampaign &&
        campaignData &&
        !campaignData.track_released
      );
      const placements: CampaignSongPlacement[] = [];

      try {
        for (const currentPlaylistId of playlistIds) {
          const playlistSession = usesMultiProfilePlaylistMode
            ? getPlaylistSession(currentPlaylistId)
            : null;
          const accessToken = playlistSession?.accessToken || loginState.token;
          const spotifyUserId = playlistSession?.spotifyUserId || loginState.userId;
          const profileObjectId = getSiblingProfileObjectIdForAdd(
            playlistSession?.profileId,
            activeProfileId
          );
          const refreshToken = profileObjectId ? '_' : loginState.refreshToken;

          if (usesMultiProfilePlaylistMode && !playlistSession) {
            const option = accountPlaylistOptions.find(
              item => item.playlistId === currentPlaylistId
            );
            toast.error(
              `Reconnect Spotify for ${option?.profileUsername || 'this profile'} before adding to this playlist.`
            );
            setLoading(false);
            return null;
          }

          const playlistItems = await getPlaylistItemsSpotify(currentPlaylistId, accessToken);
          const playlistTotal = playlistItems?.total || 0;
          let songPosition = resolvedPosition;
          if (playlistTotal === 0) {
            songPosition = 0;
          } else if (songPosition > playlistTotal) {
            songPosition = playlistTotal + 1;
          }

          const startTimeForApi = resolveStartTimeForApi({
            isFuturePlaylistPage,
            release,
            scheduleDate: setDate,
            formStartTime: values.startTime,
          });

          const response: any = await addSongToPlaylist(
            [currentPlaylistId],
            values.searchedSongData,
            accessToken,
            refreshToken,
            spotifyUserId,
            songPosition,
            startTimeForApi,
            values.endTime,
            timingMode,
            note,
            getCampaignId(),
            values.artist_name,
            values.song_name,
            profileObjectId || undefined
          );

          const playlistEntryId = Array.isArray(response)
            ? response?.[0]?.data?.data?._id
            : response?.data?.data?._id;
          if (!playlistEntryId && isFromCampaign) {
            throw new Error('Could not save the playlist entry after adding the song.');
          }

          const playlistOption = accountPlaylistOptions.find(
            option => option.playlistId === currentPlaylistId
          );
          const playlistDetails = totalPlaylist.find(playlist => playlist.id === currentPlaylistId);
          const startTimeValue = resolvePlacementStartTime({
            isFuturePlaylistPage,
            release,
            scheduleDate: setDate,
            formStartTime: values.startTime,
          });

          placements.push({
            campaignId: getCampaignId() || undefined,
            artistName: values.artist_name,
            trackName: values.song_name,
            songPosition: songPosition,
            playlistId: currentPlaylistId,
            playlistName: playlistOption?.name || playlistDetails?.name || 'Unknown Playlist',
            playlistURL:
              playlistOption?.externalUrl || playlistDetails?.external_urls?.spotify || '',
            songURI: values.searchedSongData,
            songShareLink: `https://open.spotify.com/track/${values.searchedSongData.split(':')[2]}`,
            addedByCuratorId: spotifyUserId,
            curatorName: playlistOption?.profileUsername || userName || '',
            status: timingMode,
            startTime: startTimeValue,
            endTime: values.endTime || null,
            notes: note,
            addedBy: '',
            isScheduled: isCurrentlyUnreleased,
            scheduledStartTime: isCurrentlyUnreleased && startTimeValue ? startTimeValue : null,
            playlistEntryId: playlistEntryId || undefined,
            curatorProfileObjectId:
              profileObjectId || playlistSession?.profileId || undefined,
          });
        }

        await notifyBackendAboutAddedSong(placements);
        toast.success('Song added successfully');
        setLoading(false);
        return placements[0];
      } catch (error: unknown) {
        console.error('Failed to add song to selected playlists:', error);
        toast.error(formatPlaylistAddError(error));
        setLoading(false);
        return null;
      }
    },
    [
      accountPlaylistOptions,
      activeProfileId,
      campaignData,
      getCampaignId,
      getPlaylistSession,
      isFromCampaign,
      loginState.refreshToken,
      loginState.token,
      loginState.userId,
      note,
      notifyBackendAboutAddedSong,
      release,
      setDate,
      setLoading,
      totalPlaylist,
      userName,
      usesMultiProfilePlaylistMode,
    ]
  );

  return {
    addCampaignSongToSelectedPlaylists,
    getPlaylistSession,
    isAccountPlaylistPickerLoading,
    mapCampaignPlaylistItems,
    notifyBackendAboutAddedSong,
    resetCampaignAcceptState,
    usesAccountCampaignPlaylists,
    usesAccountWidePlaylists,
    usesMultiProfilePlaylistMode,
    activeProfileId,
  };
};

export type { CampaignSongPlacement };
